import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminInterestContext from "../../context/adminInterest/adminInterestContext";
import AuthContext from "../../context/auth/authContext";
import moment from "moment";
import * as FaIcons from "react-icons/fa";
import { getReactAppUrl } from "../../functions/getEnv";
import { toast } from "react-toastify";
import { brandDomains } from "../brand";

const InterestFormRow = ({
  info: {
    _id,
    createdAt,
    age,
    campaign,
    studentFirstName,
    studentLastName,
    firstName,
    parentRelationship,
    pgRelationshipToStudent,
    parentFirstName,
    parentLastName,
    primaryEmail,
    studentEmail,
    phone,
    studentPrimaryPhone,
    lastName,
    learningCenterName,
    learningCenterAddress,
    status,
    lastContactDate,
    id = _id,
    fullName = `${studentFirstName || firstName}`,
    contactPhone = phone,
    centerName = learningCenterName,
    submissionDate = createdAt,
    Permalink,
    _uuid,
    enrollstatus,
    brandName
  },
  visibleColumns,
}) => {
  const navigate = useNavigate();
  const adminInterestContext = useContext(AdminInterestContext);
  const { ageOver, InterestNotes, registrations, gettransferRegistrations } = adminInterestContext;

  const appUrl = getReactAppUrl(import.meta.env.VITE_NODE_ENV);
  const [copiedId, setCopiedId] = useState(null);
  const authContext = useContext(AuthContext);
  const { loadUser, user } = authContext;

  // Extract userStatus from user object
  const userStatus = user?.userStatus || null;
  //console.log("userStatus",userStatus)

  // Function to get brand-specific URL based on environment
  const getBrandSpecificUrl = () => {
    const currentHostname = window.location.hostname;

    // Only do brand redirect on deployed environments (hostname starts with "enrollments.")
    // For local dev (localhost, 127.0.0.1, etc.), use current origin directly
    if (!currentHostname.startsWith('enrollments.')) {
      return window.location.origin;
    }

    // For deployed environments, use brand-specific domain if applicable
    if (brandName) {
      // Find the brand domain that matches the brandName
      const brandEntry = Object.values(brandDomains).find(
        brand => brand?.name === brandName
      );

      if (brandEntry?.domain) {
        const targetBaseDomain = brandEntry.domain;

        // Extract environment prefix dynamically from current hostname
        // Pattern: enrollments.[env-prefix.]brand-domain.org
        // Examples:
        // - enrollments.dev.llac.org -> extract 'dev.'
        // - enrollments.development.llac.org -> extract 'development.'
        // - enrollments.test.flexhigh.net -> extract 'test.'
        // - enrollments.llac.org -> extract ''

        // Remove 'enrollments.' prefix first
        const afterEnrollments = currentHostname.replace(/^enrollments\./, '');

        // Check if there's an environment prefix by comparing with known brand domains
        const allKnownDomains = Object.values(brandDomains)
          .map(brand => brand?.domain)
          .filter(Boolean);

        let envPrefix = '';

        // Check if afterEnrollments starts with a known brand domain
        const matchesKnownDomain = allKnownDomains.some(domain =>
          afterEnrollments === domain || afterEnrollments.startsWith(domain)
        );

        if (!matchesKnownDomain) {
          // There's an environment prefix - extract everything before the brand domain
          // Find where the brand domain pattern starts
          const domainParts = afterEnrollments.split('.');

          // If we have more parts than a typical domain (e.g., llac.org has 2 parts)
          // then the extra parts at the beginning are the environment prefix
          if (domainParts.length > 2) {
            // Extract the environment prefix (e.g., 'dev', 'test', 'development', 'staging')
            const prefixParts = domainParts.slice(0, -2); // Everything except last 2 parts
            envPrefix = prefixParts.join('.') + '.';
          }
        }

        return `https://enrollments.${envPrefix}${targetBaseDomain}`;
      }
    }

    // Fallback to current origin (works for localhost, dev, staging, production)
    return window.location.origin;
  };

  const handleCopy = (_id, permaLink) => {
    const brandSpecificUrl = getBrandSpecificUrl();
    const PermaLink = `${brandSpecificUrl}/register-interest?uuid=${_uuid}`;
    navigator.clipboard.writeText(PermaLink);
    setCopiedId(_id);
    setTimeout(() => setCopiedId(null), 1000);
  };

  // Find the matching note for the current interest by _id or id
  const matchingNote = InterestNotes.find(
    (note) => note.interestId === _id || note.interestId === id
  );

  const updatedStatus = matchingNote ? matchingNote.status : status;

  // Show lastContactDate if it exists and is different from createdAt (by at least 1 minute)
  // This ensures we only show it when it was actually updated via a contact note
  const updatedLastContactDate = lastContactDate &&
    Math.abs(moment(lastContactDate).diff(moment(createdAt), 'minutes')) >= 1
    ? moment(lastContactDate).format("MM/DD/YYYY")
    : "";

  // Helper function to check if column is visible
  const isColumnVisible = (columnName) => {
    if (!visibleColumns) return true; // Show all if no visibility settings
    return visibleColumns[columnName] !== false;
  };

  return (
    <tr
      onClick={() => {
        if (status === "Enrollment Submitted") {
          navigate(`/registration-forms/${_id}`);
        }
        else if (enrollstatus === "Yes") {
          toast.error("Already enrollment done, you can't edit the interest forms");
          //alert("Already enrollment done, you can't edit the interest forms");
          navigate(`/interest-forms`);
        }
        else if (userStatus === "User") {
          toast.error("You don't have edit option");
         // alert("You don't have edit option");
          navigate(`/interest-forms`);
        }
        else {
          navigate(`/interest-forms/${_id || id}`);
        }
      }}
    >
      {isColumnVisible("createdAt") && <td>{moment(createdAt || submissionDate).format("MM/DD/YYYY")}</td>}
      {isColumnVisible("learningCenterName") && <td>{learningCenterName || centerName}</td>}
      {isColumnVisible("studentFirstName") && (
        <td>
          {studentFirstName
            ? `${studentFirstName} ${studentLastName}`
            : `${firstName} ${lastName}`}
        </td>
      )}
      {isColumnVisible("age") && <td>{age}</td>}
      {!ageOver && isColumnVisible("parentRelationship") && (
        <td>
          {age >= 18
            ? ""
            : `${pgRelationshipToStudent || parentRelationship} - ${parentFirstName || firstName
            } ${parentLastName || lastName}`}
        </td>
      )}
      {isColumnVisible("phone") && <td>{phone || studentPrimaryPhone}</td>}
      {isColumnVisible("primaryEmail") && <td>{primaryEmail || studentEmail}</td>}
      {isColumnVisible("status") && <td>{updatedStatus}</td>}
      {isColumnVisible("lastContactDate") && <td>{updatedLastContactDate}</td>}
      {/* <td>{campaign}</td> */}
      {isColumnVisible("Permalink") && (
        <td style={{ position: "relative", textAlign: "center" }}>
          {copiedId === _id && (
            <div
              style={{
                position: "absolute",
                top: "-25px",
                left: "10px",
                backgroundColor: "#d4edda",
                color: "#155724",
                padding: "5px 10px",
                borderRadius: "5px",
                fontSize: "12px",
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
                whiteSpace: "nowrap",
              }}
            >
              Copied!
            </div>
          )}
          {enrollstatus !== "Yes" ? (
            <button
              className={`btn btn-llac-light-grey`}
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(_id, Permalink);
              }}
              style={{

                padding: "0px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Copy Enrollment Link
            </button>
          ) : (
            <span style={{ fontSize: "12px", color: "#6c757d" }}>
              Already Enrolled
            </span>
          )}
        </td>
      )}
    </tr>
  );
};

export default InterestFormRow;